import React from 'react';

import {configureMapColumnNamesModalPane} from 'bulk-import/panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_NAME,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {
  addNewSamplesForInputTableImport,
  validateNewSamplesForInputTableImport,
} from 'workflows/input-tables/InputTableImportActions';

export function getInputTableFlowRequiredColumnsError(
  columns: Array<SpreadsheetColumn>,
  {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
): string | null {
  const stageRunError = SpreadsheetBulkImportUtil.getRequiredFieldError(
    [COLUMN_TYPE.STAGE_RUN_NAME],
    columns,
    selectedSchemaIds,
    IMPORT_TYPE.INPUT_TABLE
  );
  if (stageRunError) {
    return stageRunError;
  }

  const numBatchNameColumns = columns.filter(({type}) => type === COLUMN_TYPE.BATCH_NAME).length;
  const numBioentityLabelColumns = columns.filter(({type}) => type === COLUMN_TYPE.FILENAME).length;

  const batchNameColumnName = COLUMN_TYPE_TO_NAME[COLUMN_TYPE.BATCH_NAME];
  const bioentityLabelColumnName = COLUMN_TYPE_TO_NAME[COLUMN_TYPE.FILENAME];

  if (numBatchNameColumns === 0 && numBioentityLabelColumns === 0) {
    return `Either a "${batchNameColumnName}" or a "${bioentityLabelColumnName}" field is required.`;
  } else if (numBatchNameColumns > 0 && numBioentityLabelColumns > 0) {
    return `A "${batchNameColumnName}" and a "${bioentityLabelColumnName}" field cannot both be provided.`;
  } else if (numBatchNameColumns > 1) {
    return `There must be exactly one "${batchNameColumnName}" field.`;
  } else if (numBioentityLabelColumns > 1) {
    return `There must be exactly one "${bioentityLabelColumnName}" field.`;
  } else {
    return null;
  }
}

interface BulkAddSamplesToInputTableFlowProps {
  onRequestClose: () => void;
  stageEntryId: string;
}

class BulkAddSamplesToInputTableFlow extends React.Component<BulkAddSamplesToInputTableFlowProps> {
  static open(props: Omit<BulkAddSamplesToInputTableFlowProps, 'onRequestClose'>): void {
    LegacyModalContainer.open(
      SPREADSHEET_BULK_IMPORT_KEY,
      <BulkAddSamplesToInputTableFlow {...props} onRequestClose={SpreadsheetBulkImportActions.closeModal} />
    );
  }

  _getPanes(): Array<Pane> {
    return [
      {
        paneKey: PaneKey.UPLOAD_SPREADSHEET,
        configuration: UploadSpreadsheetModalPane.configure({}),
      },
      {
        paneKey: PaneKey.MAP_COLUMN_NAMES,
        configuration: configureMapColumnNamesModalPane({
          getValidColumnTypes(): Array<COLUMN_TYPE> {
            return [
              COLUMN_TYPE.STAGE_RUN_NAME,
              COLUMN_TYPE.BATCH_NAME,
              COLUMN_TYPE.FILENAME,
              COLUMN_TYPE.IGNORE,
            ];
          },
          getRequiredColumnsError(
            columns: Array<SpreadsheetColumn>,
            {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
          ): string | null {
            return getInputTableFlowRequiredColumnsError(columns, {selectedSchemaIds});
          },
          onValidateSpreadsheet: () => {
            validateNewSamplesForInputTableImport(this.props.stageEntryId);
          },
        }),
      },
      {
        paneKey: PaneKey.SHOW_ERRORS,
        configuration: ShowErrorsModalPane.configure({
          enableReplaceLinkWithText: true,
          enableSkipErrors: false,
        }),
      },
    ];
  }

  _handleImportFromSpreadsheet = (): void => {
    addNewSamplesForInputTableImport(this.props.stageEntryId);
  };

  override render(): JSX.Element {
    return (
      <SpreadsheetBulkImportModalContainer
        importItemWord="input sample"
        importType={IMPORT_TYPE.INPUT_TABLE}
        onImportFromSpreadsheet={this._handleImportFromSpreadsheet}
        onRequestClose={this.props.onRequestClose}
        panes={this._getPanes()}
        title="Import into input table"
      />
    );
  }
}

export default BulkAddSamplesToInputTableFlow;
